import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import './css/lead-edit.css'
import QuotesTab from '../components/CRM/QuotesTab.jsx';
import InvoicesTab from '../components/CRM/InvoiceTab.jsx';
// Add these constants at the top of EditLeadModal
const FOLLOW_UP_ACTION = {
  "Call Customer": 0,
  "Not answering calls": 10,
  "Asking callback repeatedly": 20,
  "Shared requirements": 40,
  "Shared floor plan": 50,
  "Discussed budget openly": 60,
  "Asked for pre-estimate": 65,
  "Requested site visit": 75,
  "Site visit completed": 80,
  "Asked for revised estimate": 85,
  "Negotiation ongoing": 90,
  "Discussing start date": 95,
  "Ready to start / Advance": 100,
};

// Reverse mapping for probability to action
const PROBABILITY_TO_ACTION = {
  0: "Call Customer",
  10: "Not answering calls",
  20: "Asking callback repeatedly",
  40: "Shared requirements",
  50: "Shared floor plan",
  60: "Discussed budget openly",
  65: "Asked for pre-estimate",
  75: "Requested site visit",
  80: "Site visit completed",
  85: "Asked for revised estimate",
  90: "Negotiation ongoing",
  95: "Discussing start date",
  100: "Ready to start / Advance"
};

function getLeadCategory(score) {
  if (score <= 20) return "Cold Lead";
  if (score <= 40) return "Warm Lead";
  if (score <= 60) return "Interested Lead";
  if (score <= 80) return "Hot Lead";
  return "High conversion";
}

function getStageFromCategory(category) {
  const stageMap = {
    "Cold Lead": "New Lead",
    "Warm Lead": "Qualified",
    "Interested Lead": "Proposal",
    "Hot Lead": "Negotiation",
    "High conversion": "Won"
  };
  return stageMap[category] || "New Lead";
}

const renderFormattedNote = (text) => {
  if (!text) return null;

  // Pattern to find date entries concatenated together without line breaks
  // e.g. "27th june", "29th june-", "30th june:", "1st july", "09/07/2026", "24 Jul"
  const dateLookaheadPattern = /(?<=[^\n])\s*(?=\b\d{1,2}(?:st|nd|rd|th)?\s+(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\b|\b\d{1,2}[\/\.-]\d{1,2}(?:[\/\.-]\d{2,4})?\b)/gi;

  const formattedStr = text.replace(dateLookaheadPattern, '\n');
  const lines = formattedStr.split('\n').map(l => l.trim()).filter(Boolean);

  if (lines.length <= 1) {
    return (
      <div style={{ marginTop: '4px', whiteSpace: 'pre-wrap', lineHeight: '1.5', color: '#374151' }}>
        {text}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '6px' }}>
      {lines.map((line, idx) => (
        <div 
          key={idx} 
          style={{ 
            padding: '8px 12px', 
            backgroundColor: '#F9FAFB', 
            borderRadius: '6px', 
            borderLeft: '3px solid #7C3AED',
            fontSize: '12px',
            color: '#374151',
            lineHeight: '1.5',
            boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
          }}
        >
          {line}
        </div>
      ))}
    </div>
  );
};

// Stage to Stage Status mapping based on the image
const STAGE_STATUS_MAP = {
  "New Lead": ["Contact Pending", "No Response", "Call Back Later", "Qualification in Progress"],
  "Qualified": ["Requirement Discussion", "Waiting for Floor Plan", "Site Visit Scheduled", "Site Visit Completed", "Information Collection", "Call Back Later", "No Response", "Proposal Handover"],
  "Proposal": ["Assigned to Sales", "Proposal in Progress", "Proposal Ready", "Proposal Shared", "Call Back Later", "No Response"],
  "Negotiation": ["Waiting for Decision", "Price Discussion", "Scope Revision", "Call Back Later", "No Response"],
  "Won": ["Won"],
  "Lost": ["Lost - No Response", "Lost - Budget", "Lost - Competitor", "Lost - Postponed","Lost - Scope Of Work"],
  "Closed": ["Won", "Lost - No Response", "Lost - Budget", "Lost - Competitor", "Lost - Postponed","Lost - Scope Of Work"]
};

// Default stage status for each stage
const DEFAULT_STAGE_STATUS = {
  "New Lead": "Contact Pending",
  "Qualified": "Requirement Discussion",
  "Proposal": "Assigned to Sales",
  "Negotiation": "Waiting for Decision",
  "Won": "Won",
  "Lost": "Lost - No Response",
  "Closed": "Won"
};

// Reverse mapping: Stage Status to Stage
const STATUS_TO_STAGE = {
  "Contact Pending": "New Lead",
  "No Response": "New Lead",
  "Call Back Later": "New Lead",
  "Qualification in Progress": "New Lead",
  "Requirement Discussion": "Qualified",
  "Waiting for Floor Plan": "Qualified",
  "Site Visit Scheduled": "Qualified",
  "Site Visit Completed": "Qualified",
  "Information Collection": "Qualified",
  "Proposal Handover": "Qualified",
  "Assigned to Sales": "Proposal",
  "Proposal in Progress": "Proposal",
  "Proposal Ready": "Proposal",
  "Proposal Shared": "Proposal",
  "Waiting for Decision": "Negotiation",
  "Price Discussion": "Negotiation",
  "Scope Revision": "Negotiation",
  "Won": "Won",
  "Lost - No Response": "Lost",
  "Lost - Budget": "Lost",
  "Lost - Competitor": "Lost",
  "Lost - Postponed": "Lost",
  "Lost - Scope Of Work": "Lost"
};

// Available stages for dropdown
const AVAILABLE_STAGES = [
  "New Lead",
  "Qualified",
  "Proposal",
  "Negotiation",
  "Won",
  "Lost",
  "Closed"
];

// All available stage statuses
const ALL_STAGE_STATUSES = [
  "Contact Pending",
  "No Response",
  "Call Back Later",
  "Qualification in Progress",
  "Requirement Discussion",
  "Waiting for Floor Plan",
  "Site Visit Scheduled",
  "Site Visit Completed",
  "Information Collection",
  "Proposal Handover",
  "Assigned to Sales",
  "Proposal in Progress",
  "Proposal Ready",
  "Proposal Shared",
  "Waiting for Decision",
  "Price Discussion",
  "Scope Revision",
  "Won",
  "Lost - No Response",
  "Lost - Budget",
  "Lost - Competitor",
  "Lost - Postponed",
  "Lost - Scope Of Work"
];

// Fields that don't require a reason
const NO_REASON_FIELDS = ['notes', 'nextAction', 'nextActionDueDate'];

// Fields that should trigger the reason modal when changed
const REASON_REQUIRED_FIELDS = ['leadCategory', 'stage', 'probability'];

const EditLeadModal = ({
  isOpen,
  leadData,
  onClose,
  onUpdate,
  allUsers = [],
  isSaving = false,
  canEditLead=false,
  userRole = 'Owner',
  customFields = [] // Add customFields prop
}) => {
  console.log('leads',leadData);
  
  const [activeTab, setActiveTab] = useState('view');
  const [formData, setFormData] = useState({});
  const [notesList, setNotesList] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [assignedTo, setAssignedTo] = useState('');
  
  // State for change reasons per field
  const [fieldReasons, setFieldReasons] = useState({});
  const [showReasonsModal, setShowReasonsModal] = useState(false);
  
  // Track which fields have been manually changed
  const [manuallyChangedFields, setManuallyChangedFields] = useState({});
  const [originalFormData, setOriginalFormData] = useState({});

  // State for custom fields
  const [customFieldsData, setCustomFieldsData] = useState({});

  // State for auto-calculated fields
  const [leadCategory, setLeadCategory] = useState('');
  const [stage, setStage] = useState('');
  const [stageStatus, setStageStatus] = useState('');
  const [followUpAction, setFollowUpAction] = useState('');
  const [probability, setProbability] = useState(0);
  
  // New state for next action and due date
  const [nextAction, setNextAction] = useState('');
  const [nextActionDueDate, setNextActionDueDate] = useState('');

  // Track if auto-update is in progress
  const [isAutoUpdating, setIsAutoUpdating] = useState(false);

  // State for expandable sections
  const [expandedSections, setExpandedSections] = useState({
    personalInfo: false,
    leadDetails: false,
    autoUpdatedFields: false,
    customFields: false
  });

  // Field labels for display
  const fieldLabels = {
    name: 'Name',
    email: 'Email',
    phone: 'Phone No',
    source: 'Source',
    sourceId: 'Source ID',
    address: 'Address',
    organization: 'Organization',
    leadStatus: 'Status',
    paymentStatus: 'Is Qualified',
    amount: 'Amount',
    assignedTo: 'Assigned To',
    followUpAction: 'Action',
    leadCategory: 'Lead Category',
    stage: 'Stage',
    stageStatus: 'Stage Status',
    notes: 'Notes',
    expectedStartDate: 'Expected Start Date',
    probability: 'Probability',
    nextAction: 'Next Action',
    nextActionDueDate: 'Next Action Due Date'
  };

  useEffect(() => {
    if (leadData && leadData._id) {
      // Get follow-up action or default
      const currentFollowUpAction = leadData.followUpAction || "Call Customer";
      const score = FOLLOW_UP_ACTION[currentFollowUpAction] || 0;
      const category = getLeadCategory(score);
      const currentStage = leadData.stage || getStageFromCategory(category);
      const currentStageStatus = leadData.stageStatus || DEFAULT_STAGE_STATUS[currentStage] || "Contact Pending";

      // Extract custom fields from leadData
      const customFieldsObj = {};
      if (leadData.customFields && typeof leadData.customFields === 'object') {
        Object.keys(leadData.customFields).forEach(key => {
          customFieldsObj[key] = leadData.customFields[key] !== undefined ? leadData.customFields[key] : '';
        });
      }

      const resolvedName =
        leadData.customFields?.full_name ||
        leadData.formData?.full_name ||
        leadData.formData?.Full_Name ||
        (leadData.formData?.name && leadData.formData?.name !== 'AD 2' ? leadData.formData?.name : null) ||
        leadData.name ||
        leadData.userId?.name ||
        '';

      const resolvedEmail =
        leadData.formData?.email ||
        leadData.customFields?.email ||
        leadData.email ||
        leadData.userId?.email ||
        '';

      const rawPhone =
        leadData.formData?.phone ||
        leadData.formData?.contact ||
        leadData.customFields?.phone ||
        leadData.phone ||
        leadData.userId?.mobile ||
        '';
      const resolvedPhone = rawPhone ? String(rawPhone).replace(/^p:/i, '').trim() : '';

      const newFormData = {
        _id: leadData._id,
        name: resolvedName,
        email: resolvedEmail,
        phone: resolvedPhone,
        source: leadData.source || leadData.formData?.source || '',
        sourceId: leadData.sourceId || leadData.formData?.sourceId || '',
        address: leadData.address || leadData.formData?.address || '',
        organization: leadData.organization || leadData.formData?.organization || '',
        leadStatus: leadData.leadStatus || 'New',
        customLeadStatus: leadData.customLeadStatus || '',
        paymentStatus: leadData.paymentStatus || 'Pending',
        amount: leadData.amount || '',
        notes: (Array.isArray(leadData?.notes) && leadData.notes.length > 0)
          ? (leadData.notes[leadData.notes.length - 1]?.text || '')
          : (leadData?.remarks || leadData?.formData?.remarks || ''),
        assignedTo: leadData.assignedTo || '',
        createdBy: leadData.createdBy || '',
        createdOn: leadData.createdOn || leadData.createdAt || '',
        updatedBy: leadData.updatedBy || '',
        updatedOn: leadData.updatedOn || leadData.updatedAt || '',
        leadCategory: leadData.leadCategory || category,
        stage: leadData.stage || currentStage,
        stageStatus: leadData.stageStatus || currentStageStatus,
        followUpAction: leadData.followUpAction || currentFollowUpAction,
        expectedStartDate: leadData.expectedStartDate || '',
        probability: leadData.probability || score,
        nextAction: leadData.nextAction || '',
        nextActionDueDate: leadData.nextActionDueDate || ''
      };

      setFormData(newFormData);
      setOriginalFormData(newFormData);
      
      setLeadCategory(leadData.leadCategory || category);
      setStage(leadData.stage || currentStage);
      setStageStatus(leadData.stageStatus || currentStageStatus);
      setFollowUpAction(leadData.followUpAction || currentFollowUpAction);
      setProbability(leadData.probability || score);
      setNextAction(leadData.nextAction || '');
      setNextActionDueDate(leadData.nextActionDueDate || '');
      setCustomFieldsData(customFieldsObj);

      // Reset field reasons and changed fields
      setFieldReasons({});
      setManuallyChangedFields({});
      setIsAutoUpdating(false);

      try {
        const savedNotes = JSON.parse(localStorage.getItem('crm_lead_notes') || '{}');
        const leadNotes = savedNotes[leadData._id] || [];
        // Prefer the notes array from the lead data (backend-stored notes)
        let resolvedNotes = Array.isArray(leadData?.notes) && leadData.notes.length > 0
          ? leadData.notes
          : leadNotes;
        // If still empty, create a synthetic note from the remarks field (for Excel-imported leads)
        if (resolvedNotes.length === 0) {
          const remarksText = leadData?.remarks || '';
          if (remarksText) {
            resolvedNotes = [{
              text: remarksText,
              date: leadData?.createdAt ? new Date(leadData.createdAt).getTime().toString() : Date.now().toString(),
              updatedBy: leadData?.createdBy || '-',
              assignedUserName: '-',
            }];
            // Also persist this in localStorage so it's available in the CRM table
            const allSavedNotes = JSON.parse(localStorage.getItem('crm_lead_notes') || '{}');
            if (!allSavedNotes[leadData._id] || allSavedNotes[leadData._id].length === 0) {
              allSavedNotes[leadData._id] = resolvedNotes;
              localStorage.setItem('crm_lead_notes', JSON.stringify(allSavedNotes));
            }
          }
        }
        setNotesList(resolvedNotes);
      } catch (e) {
        setNotesList([]);
      }

      setAssignedTo(leadData.assignedTo || '');
    }
  }, [leadData]);

  // Auto-update category, stage, status when follow-up action changes
  const handleFollowUpActionChange = (action) => {
    setIsAutoUpdating(true);
    
    const score = FOLLOW_UP_ACTION[action] || 0;
    const category = getLeadCategory(score);
    const newStage = getStageFromCategory(category);
    const newStageStatus = DEFAULT_STAGE_STATUS[newStage] || "Contact Pending";

    // Check if lead category or stage were manually changed
    const isLeadCategoryManuallyChanged = manuallyChangedFields.leadCategory;
    const isStageManuallyChanged = manuallyChangedFields.stage;

    // Update states - only auto-update if not manually changed
    setFollowUpAction(action);
    setProbability(score);
    
    if (!isLeadCategoryManuallyChanged) {
      setLeadCategory(category);
    }
    
    if (!isStageManuallyChanged) {
      setStage(newStage);
    }
    
    setStageStatus(newStageStatus);
    
    // Update form data
    setFormData(prev => ({
      ...prev,
      followUpAction: action,
      probability: score,
      ...(!isLeadCategoryManuallyChanged && { leadCategory: category }),
      ...(!isStageManuallyChanged && { stage: newStage }),
      stageStatus: newStageStatus
    }));

    // Track manual changes for followUpAction
    const originalFollowUpAction = originalFormData.followUpAction;
    if (originalFollowUpAction !== action) {
      setManuallyChangedFields(prev => ({ ...prev, followUpAction: true }));
      if (!fieldReasons.followUpAction) {
        setFieldReasons(prev => ({ ...prev, followUpAction: '' }));
      }
    } else {
      setManuallyChangedFields(prev => {
        const newChanged = { ...prev };
        delete newChanged.followUpAction;
        return newChanged;
      });
      setFieldReasons(prev => {
        const newReasons = { ...prev };
        delete newReasons.followUpAction;
        return newReasons;
      });
    }

    // Remove probability from manual changes since it's auto-updated
    setManuallyChangedFields(prev => {
      const newChanged = { ...prev };
      delete newChanged.probability;
      return newChanged;
    });
    setFieldReasons(prev => {
      const newReasons = { ...prev };
      delete newReasons.probability;
      return newReasons;
    });

    // Always remove stageStatus from manual changes (it's auto-calculated)
    setManuallyChangedFields(prev => {
      const newChanged = { ...prev };
      delete newChanged.stageStatus;
      return newChanged;
    });
    setFieldReasons(prev => {
      const newReasons = { ...prev };
      delete newReasons.stageStatus;
      return newReasons;
    });

    setIsAutoUpdating(false);
  };

  // Handle probability change
  const handleProbabilityChange = (value) => {
    const numValue = Number(value);
    if (isNaN(numValue) || numValue < 0 || numValue > 100) {
      toast.error('Probability must be between 0 and 100');
      return;
    }

    setIsAutoUpdating(true);
    
    // Find the closest matching action based on probability
    const probabilities = Object.keys(PROBABILITY_TO_ACTION).map(Number);
    const closestProbability = probabilities.reduce((prev, curr) => {
      return Math.abs(curr - numValue) < Math.abs(prev - numValue) ? curr : prev;
    });
    
    const action = PROBABILITY_TO_ACTION[closestProbability];
    const category = getLeadCategory(closestProbability);
    const newStage = getStageFromCategory(category);
    const newStageStatus = DEFAULT_STAGE_STATUS[newStage] || "Contact Pending";

    // Check if lead category or stage were manually changed
    const isLeadCategoryManuallyChanged = manuallyChangedFields.leadCategory;
    const isStageManuallyChanged = manuallyChangedFields.stage;

    // Update states - only auto-update if not manually changed
    setProbability(numValue);
    setFollowUpAction(action);
    
    if (!isLeadCategoryManuallyChanged) {
      setLeadCategory(category);
    }
    
    if (!isStageManuallyChanged) {
      setStage(newStage);
    }
    
    setStageStatus(newStageStatus);
    
    // Update form data
    setFormData(prev => ({
      ...prev,
      probability: numValue,
      followUpAction: action,
      ...(!isLeadCategoryManuallyChanged && { leadCategory: category }),
      ...(!isStageManuallyChanged && { stage: newStage }),
      stageStatus: newStageStatus
    }));

    // Track manual changes for probability - this is a MANUAL change
    const originalProbability = originalFormData.probability;
    if (originalProbability !== numValue) {
      setManuallyChangedFields(prev => ({ ...prev, probability: true }));
      if (!fieldReasons.probability) {
        setFieldReasons(prev => ({ ...prev, probability: '' }));
      }
    } else {
      setManuallyChangedFields(prev => {
        const newChanged = { ...prev };
        delete newChanged.probability;
        return newChanged;
      });
      setFieldReasons(prev => {
        const newReasons = { ...prev };
        delete newReasons.probability;
        return newReasons;
      });
    }

    // Remove followUpAction from manual changes (it's auto-calculated from probability)
    setManuallyChangedFields(prev => {
      const newChanged = { ...prev };
      delete newChanged.followUpAction;
      return newChanged;
    });
    setFieldReasons(prev => {
      const newReasons = { ...prev };
      delete newReasons.followUpAction;
      return newReasons;
    });

    // Always remove stageStatus from manual changes (it's auto-calculated)
    setManuallyChangedFields(prev => {
      const newChanged = { ...prev };
      delete newChanged.stageStatus;
      return newChanged;
    });
    setFieldReasons(prev => {
      const newReasons = { ...prev };
      delete newReasons.stageStatus;
      return newReasons;
    });

    setIsAutoUpdating(false);
  };

  // Handle manual stage status change from dropdown - Stage becomes disabled
  const handleStageStatusChange = (status) => {
    setIsAutoUpdating(true);
    
    // Auto-select the corresponding stage based on the status
    const newStage = STATUS_TO_STAGE[status] || stage;
    
    setStageStatus(status);
    setStage(newStage);
    setFormData(prev => ({ 
      ...prev, 
      stageStatus: status,
      stage: newStage 
    }));
    
    // Track manual changes for stage status
    const originalStatus = originalFormData.stageStatus;
    if (originalStatus !== status) {
      setManuallyChangedFields(prev => ({ ...prev, stageStatus: true }));
      if (!fieldReasons.stageStatus) {
        setFieldReasons(prev => ({ ...prev, stageStatus: '' }));
      }
    } else {
      setManuallyChangedFields(prev => {
        const newChanged = { ...prev };
        delete newChanged.stageStatus;
        return newChanged;
      });
      setFieldReasons(prev => {
        const newReasons = { ...prev };
        delete newReasons.stageStatus;
        return newReasons;
      });
    }
    
    // Stage is auto-updated, so remove from manually changed if it was there
    setManuallyChangedFields(prev => {
      const newChanged = { ...prev };
      delete newChanged.stage;
      return newChanged;
    });
    setFieldReasons(prev => {
      const newReasons = { ...prev };
      delete newReasons.stage;
      return newReasons;
    });
    
    setIsAutoUpdating(false);
  };

  // Handle manual stage change from dropdown - Stage is now editable
  const handleStageChange = (newStage) => {
    setIsAutoUpdating(true);
    
    // Update stage
    setStage(newStage);
    const newStatus = newStage === 'Lost' ? 'Lost' : (newStage === 'Won' ? 'Won' : (newStage === 'Qualified' ? 'Qualified' : (newStage === 'Proposal' ? 'Proposal' : (newStage === 'Negotiation' ? 'Negotiation' : formData.leadStatus))));
    
    setFormData(prev => ({ 
      ...prev, 
      stage: newStage,
      leadStatus: newStatus
    }));
    
    // Auto-update stage status based on new stage
    const newStageStatus = DEFAULT_STAGE_STATUS[newStage] || "Contact Pending";
    setStageStatus(newStageStatus);
    setFormData(prev => ({ 
      ...prev, 
      stageStatus: newStageStatus 
    }));
    
    // Track manual changes for stage
    const originalStage = originalFormData.stage;
    if (originalStage !== newStage) {
      setManuallyChangedFields(prev => ({ ...prev, stage: true }));
      if (!fieldReasons.stage) {
        setFieldReasons(prev => ({ ...prev, stage: '' }));
      }
    } else {
      setManuallyChangedFields(prev => {
        const newChanged = { ...prev };
        delete newChanged.stage;
        return newChanged;
      });
      setFieldReasons(prev => {
        const newReasons = { ...prev };
        delete newReasons.stage;
        return newReasons;
      });
    }
    
    // Stage status is auto-updated, so remove from manually changed if it was there
    setManuallyChangedFields(prev => {
      const newChanged = { ...prev };
      delete newChanged.stageStatus;
      return newChanged;
    });
    setFieldReasons(prev => {
      const newReasons = { ...prev };
      delete newReasons.stageStatus;
      return newReasons;
    });
    
    setIsAutoUpdating(false);
  };

  // Handle manual lead category change from dropdown
  const handleLeadCategoryChange = (category) => {
    setLeadCategory(category);
    setFormData(prev => ({ ...prev, leadCategory: category }));
    // This is a manual change, so track it
    const originalCategory = originalFormData.leadCategory;
    if (originalCategory !== category) {
      setManuallyChangedFields(prev => ({ ...prev, leadCategory: true }));
      if (!fieldReasons.leadCategory) {
        setFieldReasons(prev => ({ ...prev, leadCategory: '' }));
      }
    } else {
      setManuallyChangedFields(prev => {
        const newChanged = { ...prev };
        delete newChanged.leadCategory;
        return newChanged;
      });
      setFieldReasons(prev => {
        const newReasons = { ...prev };
        delete newReasons.leadCategory;
        return newReasons;
      });
    }
  };

  // Handle custom field change
  const handleCustomFieldChange = (fieldKey, value) => {
    setCustomFieldsData(prev => ({ ...prev, [fieldKey]: value }));
    // Track manual changes for custom fields
    const originalValue = leadData?.customFields?.[fieldKey] || '';
    if (originalValue !== value) {
      setManuallyChangedFields(prev => ({ ...prev, [`custom_${fieldKey}`]: true }));
      if (!fieldReasons[`custom_${fieldKey}`]) {
        setFieldReasons(prev => ({ ...prev, [`custom_${fieldKey}`]: '' }));
      }
    } else {
      setManuallyChangedFields(prev => {
        const newChanged = { ...prev };
        delete newChanged[`custom_${fieldKey}`];
        return newChanged;
      });
      setFieldReasons(prev => {
        const newReasons = { ...prev };
        delete newReasons[`custom_${fieldKey}`];
        return newReasons;
      });
    }
  };

  // Track manual field changes (excluding notes and auto fields)
  const handleFieldChange = (field, value) => {
    // Don't track notes, nextAction, nextActionDueDate
    if (['notes', 'nextAction', 'nextActionDueDate'].includes(field)) {
      setFormData(prev => ({ ...prev, [field]: value }));
      if (field === 'nextAction') setNextAction(value);
      if (field === 'nextActionDueDate') setNextActionDueDate(value);
      return;
    }

    // Don't track if auto-updating
    if (isAutoUpdating) return;

    setFormData(prev => ({ ...prev, [field]: value }));
    
    const originalValue = originalFormData[field];
    if (originalValue !== value) {
      setManuallyChangedFields(prev => ({ ...prev, [field]: true }));
      if (!fieldReasons[field]) {
        setFieldReasons(prev => ({ ...prev, [field]: '' }));
      }
    } else {
      setManuallyChangedFields(prev => {
        const newChanged = { ...prev };
        delete newChanged[field];
        return newChanged;
      });
      setFieldReasons(prev => {
        const newReasons = { ...prev };
        delete newReasons[field];
        return newReasons;
      });
    }
  };

  const handleSaveNote = () => {
    if (newNote.trim() && leadData && leadData._id) {
      const noteObj = {
        text: newNote.trim(),
        date: new Date().toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      };

      const updatedNotes = [...notesList, noteObj];
      setNotesList(updatedNotes);

      try {
        const allSavedNotes = JSON.parse(localStorage.getItem('crm_lead_notes') || '{}');
        allSavedNotes[leadData._id] = updatedNotes;
        localStorage.setItem('crm_lead_notes', JSON.stringify(allSavedNotes));
        toast.success('Note added successfully!');
      } catch (e) {
        console.error("Failed to save note", e);
        toast.error('Failed to save note');
      }

      setIsAddingNote(false);
      setNewNote('');
    }
  };

  const handleUpdateLead = async () => {
    // Get fields that need reasons (only leadCategory, stage, probability that were manually changed)
    const needReasonFields = Object.keys(manuallyChangedFields).filter(
      field => REASON_REQUIRED_FIELDS.includes(field)
    );
    
    const missingReasons = needReasonFields.filter(key => !fieldReasons[key] || !fieldReasons[key].trim());
    
    if (missingReasons.length > 0) {
      toast.error(`Please provide reasons for: ${missingReasons.map(k => fieldLabels[k] || k).join(', ')}`);
      return;
    }

    // Combine all reasons into a single string
    const combinedReasons = needReasonFields
      .filter(key => fieldReasons[key] && fieldReasons[key].trim())
      .map(key => `${fieldLabels[key] || key}: ${fieldReasons[key]}`)
      .join('; ');

    // Check if there is a note in formData.notes that needs to be added to notesList / history
    let finalNotesList = [...notesList];
    const editedNoteText = (formData.notes || '').trim();
    const lastNoteText = notesList.length > 0 ? (notesList[notesList.length - 1]?.text || '').trim() : '';

    if (editedNoteText && editedNoteText !== lastNoteText) {
      const newNoteObj = {
        text: editedNoteText,
        date: Date.now().toString(),
        updatedBy: leadData?.updatedBy || '-',
        assignedUserName: allUsers?.find((val) => val._id === assignedTo)?.profile?.name || '-'
      };
      finalNotesList = [...notesList, newNoteObj];
      setNotesList(finalNotesList);

      try {
        const allSavedNotes = JSON.parse(localStorage.getItem('crm_lead_notes') || '{}');
        allSavedNotes[formData._id] = finalNotesList;
        localStorage.setItem('crm_lead_notes', JSON.stringify(allSavedNotes));
      } catch (e) {
        console.error("Failed to save note to localStorage", e);
      }
    }

    const payload = {
      _id: formData._id,
      formData: {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        organization: formData.organization,
        notes: editedNoteText,
      },
      notes: finalNotesList,
      leadStatus: (stage === 'Lost' || stageStatus?.startsWith('Lost')) ? 'Lost' : (stage === 'Won' ? 'Won' : (stage === 'Qualified' ? 'Qualified' : (stage === 'Proposal' ? 'Proposal' : (stage === 'Negotiation' ? 'Negotiation' : (formData.leadStatus === 'Others' ? formData.customLeadStatus : (formData.leadStatus || stage)))))),
      paymentStatus: formData.paymentStatus,
      amount: formData.amount,
      assignedTo: assignedTo,
      assignedUserName: allUsers?.find((val) => val._id === assignedTo)?.profile?.name || '',
      followUpAction: followUpAction,
      probability: probability,
      leadCategory: leadCategory,
      stage: stage,
      stageStatus: stageStatus,
      expectedStartDate: formData.expectedStartDate || '',
      nextAction: nextAction,
      nextActionDueDate: nextActionDueDate,
      changeReason: combinedReasons, // Combined reasons as single string
      customFields: customFieldsData // Include custom fields in payload
    };

    await onUpdate(payload);
    setShowReasonsModal(false);
    setFieldReasons({});
    setManuallyChangedFields({});
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return '-';
    }
  };

  // Open reasons modal before saving
  const handleSaveWithReasons = () => {
    // Only check for leadCategory, stage, probability that were manually changed
    const changedFieldKeys = Object.keys(manuallyChangedFields).filter(
      field => REASON_REQUIRED_FIELDS.includes(field)
    );
    
    if (changedFieldKeys.length === 0) {
      // No reason-required fields changed - save directly
      handleUpdateLead();
      return;
    }
    setShowReasonsModal(true);
  };

  // Update reason for a specific field
  const handleReasonChange = (field, value) => {
    setFieldReasons(prev => ({ ...prev, [field]: value }));
  };

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (!isOpen) return null;

  // Get fields that need reasons (only leadCategory, stage, probability)
  const changedFieldKeys = Object.keys(manuallyChangedFields).filter(
    field => REASON_REQUIRED_FIELDS.includes(field)
  );

  // Get available stage statuses for the current stage
  const getAvailableStageStatuses = (currentStage) => {
    return STAGE_STATUS_MAP[currentStage] || ALL_STAGE_STATUSES;
  };

  // Get custom field value or default
  const getCustomFieldValue = (fieldKey) => {
    return customFieldsData[fieldKey] || '';
  };

  // Get custom field label
  const getCustomFieldLabel = (fieldKey) => {
    const field = customFields.find(f => f.fieldKey === fieldKey);
    return field?.fieldName || fieldKey;
  };

  // Get custom field type
  const getCustomFieldType = (fieldKey) => {
    const field = customFields.find(f => f.fieldKey === fieldKey);
    return field?.fieldType || 'text';
  };

  // Get custom field options (for dropdown, radio, checkbox)
  const getCustomFieldOptions = (fieldKey) => {
    const field = customFields.find(f => f.fieldKey === fieldKey);
    return field?.options || [];
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: 'rgba(0,0,0,0.5)',
      zIndex: 1000,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <div style={{
        backgroundColor: '#fff',
        borderRadius: '12px',
        width: '1180px',
        maxWidth: '96vw',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}>
        {/* Modal Header */}
        <div style={{
          padding: '20px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #F3F4F6'
        }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#111827' }}>
            Lead Details
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#6B7280',
              fontSize: '20px'
            }}
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #F3F4F6',
          padding: '0 24px'
        }}>
          <button
            onClick={() => setActiveTab('view')}
            style={{
              padding: '12px 20px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: activeTab === 'view' ? '600' : '500',
              color: activeTab === 'view' ? '#7C3AED' : '#6B7280',
              borderBottom: activeTab === 'view' ? '2px solid #7C3AED' : '2px solid transparent',
              transition: 'all 0.2s'
            }}
          >
            View
          </button>
          {canEditLead&&<button
            onClick={() => setActiveTab('edit')}
            style={{
              padding: '12px 20px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: activeTab === 'edit' ? '600' : '500',
              color: activeTab === 'edit' ? '#7C3AED' : '#6B7280',
              borderBottom: activeTab === 'edit' ? '2px solid #7C3AED' : '2px solid transparent',
              transition: 'all 0.2s'
            }}
          >
            Edit
          </button>}
          <button
    onClick={() => setActiveTab('quotes')}
    style={{
      padding: '12px 20px',
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: activeTab === 'quotes' ? '600' : '500',
      color: activeTab === 'quotes' ? '#7C3AED' : '#6B7280',
      borderBottom: activeTab === 'quotes' ? '2px solid #7C3AED' : '2px solid transparent',
      transition: 'all 0.2s',
      whiteSpace: 'nowrap'
    }}
  >
    Quotes
  </button>
    <button
    onClick={() => setActiveTab('invoices')}
    style={{
      padding: '12px 20px',
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: activeTab === 'invoices' ? '600' : '500',
      color: activeTab === 'invoices' ? '#7C3AED' : '#6B7280',
      borderBottom: activeTab === 'invoices' ? '2px solid #7C3AED' : '2px solid transparent',
      transition: 'all 0.2s',
      whiteSpace: 'nowrap'
    }}
  >
    Invoices
  </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
          {activeTab === 'view' && (
            // View Tab
            <div>
              {/* Lead Info Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '16px',
                marginBottom: '24px',
                backgroundColor: '#F9FAFB',
                padding: '16px',
                borderRadius: '8px'
              }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Lead ID</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                    {formData._id ? formData._id.slice(-6).toUpperCase() : '-'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Created Time</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                    {leadData?.createdTime? leadData?.createdTime : '-'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Name</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                    {formData.name || '-'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Email</div>
                  <div style={{ fontSize: '14px', color: '#374151' }}>
                    {formData.email || '-'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Phone</div>
                  <div style={{ fontSize: '14px', color: '#374151' }}>
                    {formData.phone || '-'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Platform</div>
                  <div style={{ fontSize: '14px', color: '#374151' }}>
                    {formData.source || '-'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>When do you want to start your interior work?</div>
                  <div style={{ fontSize: '14px', color: '#374151' }}>
                    {formData.sourceId || '-'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>City</div>
                  <div style={{ fontSize: '14px', color: '#374151' }}>
                    {formData.address || '-'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Organization</div>
                  <div style={{ fontSize: '14px', color: '#374151' }}>
                    {formData.organization || '-'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Status</div>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: formData.leadStatus === 'New' ? '#059669' :
                      formData.leadStatus === 'Connected' ? '#7C3AED' :
                        formData.leadStatus === 'Lost' ? '#DC2626' :
                          formData.leadStatus === 'Interested' ? '#D97706' :
                            '#374151'
                  }}>
                    {formData.leadStatus || '-'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Is Qualified</div>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: formData.paymentStatus === 'Successful' ? '#16A34A' :
                      formData.paymentStatus === 'Failed' ? '#DC2626' :
                        '#D97706'
                  }}>
                    {formData.paymentStatus || '-'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Budget</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                    {formData.amount ? `₹${formData.amount}` : '-'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Assigned To</div>
                  <div style={{ fontSize: '14px', color: '#374151' }}>
                    {assignedTo ? allUsers.find(u => u._id === assignedTo)?.profile?.name || assignedTo : '-'}
                  </div>
                </div>
                {/* New fields in view mode */}
                <div>
                  <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Probability</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                    {probability !== undefined && probability !== null ? `${probability}%` : '-'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Lead Category</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                    {leadCategory || '-'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Stage</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                    {stage || '-'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Stage Status</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                    {stageStatus || '-'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Action</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                    {followUpAction || '-'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Next Action</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                    {nextAction || '-'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Next Action Due Date</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                    {nextActionDueDate ? new Date(nextActionDueDate).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric'
                    }) : '-'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Expected Start Date</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                    {formData.expectedStartDate ? new Date(formData.expectedStartDate).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric'
                    }) : '-'}
                  </div>
                </div>

                {/* Custom Fields in View Mode */}
                {Object.keys(leadData?.customFields).length > 0 && (
                  <>
                    <div style={{ gridColumn: '1 / -1', marginTop: '8px' }}>
                      {/* <hr style={{ border: '1px solid #E5E7EB', margin: '8px 0' }} /> */}
                      {/* <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827', marginBottom: '12px' }}>
                        Custom Fields
                      </div> */}
                    </div>
                    {Object.keys(leadData?.customFields).map((fieldKey) => {
                      const value = getCustomFieldValue(fieldKey);
                      
                      return (
                        <div key={fieldKey}>
                          <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>
                            {fieldKey}
                          </div>
                          <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                            {value || '-'}
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>

              {/* Created & Updated Info */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '16px',
                marginBottom: '24px',
                backgroundColor: '#F3F4F6',
                padding: '12px 16px',
                borderRadius: '8px'
              }}>
                <div>
                  <div style={{ fontSize: '11px', color: '#6B7280', marginBottom: '2px' }}>Created On</div>
                  <div style={{ fontSize: '13px', color: '#374151' }}>
                    {formatDate(formData.createdOn)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#6B7280', marginBottom: '2px' }}>Created By</div>
                  <div style={{ fontSize: '13px', color: '#374151' }}>
                    {formData.createdBy ? allUsers.find(u => u._id === formData.createdBy)?.profile?.name || formData.createdBy : '-'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#6B7280', marginBottom: '2px' }}>Updated On</div>
                  <div style={{ fontSize: '13px', color: '#374151' }}>
                    {formatDate(formData.updatedOn)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#6B7280', marginBottom: '2px' }}>Updated By</div>
                  <div style={{ fontSize: '13px', color: '#374151' }}>
                    {formData.updatedBy ? allUsers.find(u => u._id === formData.updatedBy)?.profile?.name || formData.updatedBy : '-'}
                  </div>
                </div>
              </div>

              {/* History Section */}
              <div>
                <div style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#111827',
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span>📋</span> History
                </div>
                <div style={{
                  maxHeight: '300px',
                  overflowY: 'auto',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  padding: '12px'
                }}>
                  {notesList.length > 0 ? (
                    notesList.map((note, index) => (
                      <div
                        key={index}
                        style={{
                          padding: "12px 0",
                          borderBottom: index < notesList.length - 1 ? "1px solid #F3F4F6" : "none",
                        }}
                      >
                        <div style={{ fontSize: "13px", color: "#374151" }}>
                          <strong style={{ color: "#111827" }}>Note:</strong>
                          {renderFormattedNote(note.text)}
                        </div>
                        {note?.changeReason && (
                          <div style={{ 
                            fontSize: "12px", 
                            color: "#7C3AED", 
                            marginTop: "6px",
                            backgroundColor: "#F5F3FF",
                            padding: "8px 12px",
                            borderRadius: "4px",
                            borderLeft: "3px solid #7C3AED"
                          }}>
                            <div style={{ fontWeight: "600", marginBottom: "4px", color: "#6B21A5" }}>
                              📝 Field update reason:
                            </div>
                            {note.changeReason.split('; ').map((reason, idx) => {
                              // Skip empty reasons
                              if (!reason.trim()) return null;
                              
                              // Check if reason contains a colon to format nicely
                              const parts = reason.split(': ');
                              if (parts.length === 2) {
                                return (
                                  <div 
                                    key={idx} 
                                    style={{ 
                                      display: "flex",
                                      padding: "2px 0",
                                      fontSize: "12px",
                                      color: "#374151",
                                      borderBottom: idx < note.changeReason.split('; ').filter(r => r.trim()).length - 1 ? "1px solid #E5E7EB" : "none"
                                    }}
                                  >
                                    <span style={{ 
                                      fontWeight: "600", 
                                      color: "#6B21A5",
                                      minWidth: "120px"
                                    }}>
                                      {parts[0]}:
                                    </span>
                                    <span style={{ color: "#374151" }}>
                                      {parts[1]}
                                    </span>
                                  </div>
                                );
                              }
                              
                              // Fallback for reasons without colon
                              return (
                                <div key={idx} style={{ padding: "2px 0", fontSize: "12px", color: "#374151" }}>
                                  • {reason}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <div style={{ fontSize: "11px", color: "#6B7280", marginTop: "4px" }}>
                          <strong>Updated On:</strong>{" "}
                          {note.date
                            ? new Date(Number(note.date)).toLocaleString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            })
                            : "-"}
                        </div>
                        <div style={{ fontSize: "11px", color: "#6B7280", marginTop: "2px" }}>
                          <strong>Updated By:</strong> {note.updatedBy || "-"}
                        </div>
                        <div style={{ fontSize: "11px", color: "#6B7280", marginTop: "2px" }}>
                          <strong>Assigned To:</strong> {note.assignedUserName || "-"}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ fontSize: '13px', color: '#9CA3AF', textAlign: 'center' }}>
                      No history available
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) }
          { activeTab === 'edit' &&(
            // Edit Tab - Re-arranged fields with expandable sections
            <div>
              {/* Personal Information Section with Expand/Collapse */}
              <div style={{ 
                marginBottom: '20px',
                padding: '16px',
                backgroundColor: '#F9FAFB',
                borderRadius: '8px',
                border: '1px solid #E5E7EB'
              }}>
                <div 
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    marginBottom: expandedSections.personalInfo ? '12px' : '0'
                  }}
                  onClick={() => toggleSection('personalInfo')}
                >
                  <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                    Lead Information
                  </h4>
                  <button
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#000000',
                      fontSize: '20px',
                      padding: '4px 8px'
                    }}
                  >
                    {expandedSections.personalInfo ? '−' : '+'}
                  </button>
                </div>
                
                {expandedSections.personalInfo && (
                  <div>
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>
                          Name <span style={{ color: '#EF4444' }}>*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => handleFieldChange('name', e.target.value)}
                          style={{ 
                            width: '100%', 
                            padding: '8px 12px', 
                            borderRadius: '6px', 
                            outline: 'none', 
                            fontSize: '14px', 
                            color: '#111827', 
                            boxSizing: 'border-box' 
                          }}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>
                          Email <span style={{ color: '#EF4444' }}>*</span>
                        </label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleFieldChange('email', e.target.value)}
                          style={{ 
                            width: '100%', 
                            padding: '8px 12px', 
                            borderRadius: '6px', 
                            outline: 'none', 
                            fontSize: '14px', 
                            color: '#111827', 
                            boxSizing: 'border-box' 
                          }}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>
                          Phone No <span style={{ color: '#EF4444' }}>*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.phone}
                          onChange={(e) => handleFieldChange('phone', e.target.value)}
                          style={{ 
                            width: '100%', 
                            padding: '8px 12px', 
                            borderRadius: '6px', 
                            outline: 'none', 
                            fontSize: '14px', 
                            color: '#111827', 
                            boxSizing: 'border-box' 
                          }}
                        />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>Platform</label>
                        <input
                          type="text"
                          value={formData.source || ''}
                          onChange={(e) => handleFieldChange('source', e.target.value)}
                          style={{ 
                            width: '100%', 
                            padding: '8px 12px', 
                            borderRadius: '6px', 
                            outline: 'none', 
                            fontSize: '14px', 
                            color: '#111827', 
                            boxSizing: 'border-box' 
                          }}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>When do you want to start?</label>
                        <input
                          type="text"
                          value={formData.sourceId || ''}
                          onChange={(e) => handleFieldChange('sourceId', e.target.value)}
                          style={{ 
                            width: '100%', 
                            padding: '8px 12px', 
                            borderRadius: '6px', 
                            outline: 'none', 
                            fontSize: '14px', 
                            color: '#111827', 
                            boxSizing: 'border-box' 
                          }}
                        />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>City</label>
                        <input
                          type="text"
                          value={formData.address || ''}
                          onChange={(e) => handleFieldChange('address', e.target.value)}
                          style={{ 
                            width: '100%', 
                            padding: '8px 12px', 
                            borderRadius: '6px', 
                            outline: 'none', 
                            fontSize: '14px', 
                            color: '#111827', 
                            boxSizing: 'border-box' 
                          }}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>Organization</label>
                        <input
                          type="text"
                          value={formData.organization || ''}
                          onChange={(e) => handleFieldChange('organization', e.target.value)}
                          style={{ 
                            width: '100%', 
                            padding: '8px 12px', 
                            borderRadius: '6px', 
                            outline: 'none', 
                            fontSize: '14px', 
                            color: '#111827', 
                            boxSizing: 'border-box' 
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Lead Details Section with Expand/Collapse */}
              <div style={{ 
                marginBottom: '20px',
                padding: '16px',
                backgroundColor: '#F9FAFB',
                borderRadius: '8px',
                border: '1px solid #E5E7EB'
              }}>
                <div 
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    marginBottom: expandedSections.leadDetails ? '12px' : '0'
                  }}
                  onClick={() => toggleSection('leadDetails')}
                >
                  <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                    Lead Details
                  </h4>
                  <button
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#000000',
                      fontSize: '20px',
                      padding: '4px 8px'
                    }}
                  >
                    {expandedSections.leadDetails ? '−' : '+'}
                  </button>
                </div>
                
                {expandedSections.leadDetails && (
                  <div>
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>Status</label>
                        <select
                          value={formData.leadStatus}
                          onChange={(e) => handleFieldChange('leadStatus', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            outline: 'none',
                            fontSize: '14px',
                            color: '#111827',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="New">New</option>
                          <option value="Contracted">Connected</option>
                          <option value="Interested">Interested</option>
                          <option value="Qualified">Qualified</option>
                          <option value="Lost">Lost</option>
                          <option value="Converted">Converted</option>
                          <option value="Others">Others</option>
                        </select>
                        {formData.leadStatus === 'Others' && (
                          <input
                            type="text"
                            placeholder="Enter custom status"
                            value={formData.customLeadStatus || ''}
                            onChange={(e) => handleFieldChange('customLeadStatus', e.target.value)}
                            style={{ width: '100%', marginTop: '6px', padding: '6px 12px', backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '6px', outline: 'none', fontSize: '14px', boxSizing: 'border-box' }}
                          />
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>Is Qualified</label>
                        <input
                        type='text'
                          value={formData.paymentStatus}
                          onChange={(e) => handleFieldChange('paymentStatus', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            outline: 'none',
                            fontSize: '14px',
                            color: '#111827',
                            cursor: 'pointer'
                          }}
                        />
                         
                      </div>
                      <div style={{ flex:1 }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>Budget</label>
                        <input
                          type="text"
                          value={formData.amount || ''}
                          onChange={(e) => handleFieldChange('amount', e.target.value)}
                          placeholder="Enter amount"
                          style={{ 
                            width: '100%', 
                            padding: '8px 12px', 
                            borderRadius: '6px', 
                            outline: 'none', 
                            fontSize: '14px', 
                            color: '#111827', 
                            boxSizing: 'border-box' 
                          }}
                        />
                      </div>
                    </div>

                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>
                        Expected Start Date 
                        <span className="info-tooltip">
                          ⓘ
                          <span className="tooltip-text">
                            Date the project is expected to start. This field will be moved to the top section.
                          </span>
                        </span>
                      </label>
                      <input
                        type="date"
                        value={formData.expectedStartDate?.split('T')[0] || ''}
                        onChange={(e) => handleFieldChange('expectedStartDate', e.target.value)}
                        style={{ 
                          width: '100%', 
                          padding: '8px 12px', 
                          borderRadius: '6px', 
                          outline: 'none', 
                          fontSize: '14px', 
                          color: '#111827', 
                          boxSizing: 'border-box' 
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Auto Updated Fields Section with Expand/Collapse */}
              <div style={{ 
                marginBottom: '20px',
                padding: '16px',
                backgroundColor: '#F9FAFB',
                borderRadius: '8px',
                border: '1px solid #E5E7EB'
              }}>
                <div 
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    marginBottom: expandedSections.autoUpdatedFields ? '12px' : '0'
                  }}
                  onClick={() => toggleSection('autoUpdatedFields')}
                >
                  <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                    Auto Updated Fields
                  </h4>
                  <button
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#000000',
                      fontSize: '20px',
                      padding: '4px 8px'
                    }}
                  >
                    {expandedSections.autoUpdatedFields ? '−' : '+'}
                  </button>
                </div>
                
                {expandedSections.autoUpdatedFields && (
                  <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>
                        Lead Category
                      </label>
                      <select
                        value={leadCategory}
                        onChange={(e) => handleLeadCategoryChange(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          outline: 'none',
                          fontSize: '14px',
                          color: '#111827',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="Cold Lead">Cold Lead</option>
                        <option value="Warm Lead">Warm Lead</option>
                        <option value="Interested Lead">Interested Lead</option>
                        <option value="Hot Lead">Hot Lead</option>
                        <option value="High conversion">High conversion</option>
                      </select>
                      {manuallyChangedFields.leadCategory && (
                        <div style={{ fontSize: '11px', color: '#D97706', marginTop: '4px' }}>
                          ⚠️ Field changed - please provide reason when saving
                        </div>
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>
                        Stage 
                      </label>
                      <select
                        value={stage}
                        onChange={(e) => handleStageChange(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          outline: 'none',
                          fontSize: '14px',
                          color: '#111827',
                          cursor: 'pointer'
                        }}
                      >
                        {AVAILABLE_STAGES.map((stageOption) => (
                          <option key={stageOption} value={stageOption}>
                            {stageOption}
                          </option>
                        ))}
                      </select>
                      {manuallyChangedFields.stage && (
                        <div style={{ fontSize: '11px', color: '#D97706', marginTop: '4px' }}>
                          ⚠️ Field changed - please provide reason when saving
                        </div>
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>
                        Probability (%)
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={probability}
                          onChange={(e) => {
                            // This is a manual change, so track it
                            handleProbabilityChange(e.target.value);
                          }}
                          style={{ 
                            width: '100%', 
                            padding: '8px 12px', 
                            borderRadius: '6px', 
                            outline: 'none', 
                            fontSize: '14px', 
                            color: '#111827',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                      {manuallyChangedFields.probability && (
                        <div style={{ fontSize: '11px', color: '#D97706', marginTop: '4px' }}>
                          ⚠️ Field changed - please provide reason when saving
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Lead Management Section - Always visible */}
              <div style={{ 
                marginBottom: '20px',
                padding: '16px',
                backgroundColor: '#F9FAFB',
                borderRadius: '8px',
                border: '1px solid #E5E7EB'
              }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                  Lead Management
                </h4>

                <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                  <div style={{width:"100%"}}>
                    {/* Notes Section */}
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>Add Note</label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) => handleFieldChange('notes', e.target.value)}
                        rows="5"
                        style={{ 
                          width: '100%', 
                          padding: '8px 12px', 
                          borderRadius: '6px', 
                          fontSize: '14px', 
                          color: '#111827', 
                          boxSizing: 'border-box', 
                          resize: 'vertical' 
                        }}
                        placeholder="Add notes here..."
                      />
                    </div>
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>Assigned To</label>
                      <select
                        value={assignedTo}
                        onChange={(e) => {
                          setAssignedTo(e.target.value);
                          handleFieldChange('assignedTo', e.target.value);
                        }}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          outline: 'none',
                          fontSize: '14px',
                          color: '#111827',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="">Unassigned</option>
                        {allUsers.map((user) => (
                          <option key={user._id} value={user._id}>
                            {user?.profile?.name || user?.email || 'Unknown User'}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <div style={{ flex:1 ,paddingBottom:"5px",paddingTop:"5px"}}>
                      <label style={{ display: 'block',
                         fontSize: '13px', fontWeight: '600', 
                         color: '#111827', marginBottom: '4px' }}>
                        Next Action Due Date
                      </label>
                      <input
                        type="date"
                        value={nextActionDueDate?.split('T')[0] || ''}
                        onChange={(e) => handleFieldChange('nextActionDueDate', e.target.value)}
                        style={{ 
                          width: '100%', 
                          padding: '8px 12px', 
                          borderRadius: '6px', 
                          outline: 'none', 
                          fontSize: '14px', 
                          color: '#111827', 
                          boxSizing: 'border-box' 
                        }}
                      />
                    </div>
                 
                    <div style={{ flex: 1 ,paddingBottom:"5px",paddingTop:"5px"}}>
                      <label style={{ display: 'block', 
                        fontSize: '13px', fontWeight: '600', 
                        color: '#111827', marginBottom: '4px' }}>
                        Stage Status
                      </label>
                      <select
                        value={stageStatus}
                        onChange={(e) => handleStageStatusChange(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          outline: 'none',
                          fontSize: '14px',
                          color: '#111827',
                          cursor: 'pointer'
                        }}
                      >
                        {ALL_STAGE_STATUSES.map((status) => (
                          <option key={status} value={status}>{status}</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ flex:1 ,paddingBottom:"5px",paddingTop:"5px"}}>
                      <label style={{ display: 'block',
                        fontSize: '13px', fontWeight: '600',
                        color: '#111827', marginBottom: '4px' }}>
                        Task Completed
                      </label>
                      <select
                        value={followUpAction}
                        onChange={(e) => handleFollowUpActionChange(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          outline: 'none',
                          fontSize: '14px',
                          color: '#111827',
                          cursor: 'pointer'
                        }}
                      >
                        {Object.keys(FOLLOW_UP_ACTION).map((action) => (
                          <option key={action} value={action}>
                            {action} ({FOLLOW_UP_ACTION[action]}%)
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Custom Fields Section with Expand/Collapse */}
              {Object.keys(leadData?.customFields).length > 0 && (
                <div style={{ 
                  marginBottom: '20px',
                  padding: '16px',
                  backgroundColor: '#F9FAFB',
                  borderRadius: '8px',
                  border: '1px solid #E5E7EB'
                }}>
                  <div 
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                      marginBottom: expandedSections.customFields ? '12px' : '0'
                    }}
                    onClick={() => toggleSection('customFields')}
                  >
                    <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                      Custom Fields
                    </h4>
                    <button
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#000000',
                        fontSize: '20px',
                        padding: '4px 8px'
                      }}
                    >
                      {expandedSections.customFields ? '−' : '+'}
                    </button>
                  </div>
                  
                  {expandedSections?.customFields && (
                    <div>
                      {customFields.map((field) => {
                        const value = getCustomFieldValue(field.fieldKey);
                        const fieldType = getCustomFieldType(field.fieldKey);
                        const options = getCustomFieldOptions(field.fieldKey);
                        const isManuallyChanged = manuallyChangedFields[`custom_${field.fieldKey}`];

                        return (
                          <div key={field.fieldKey} style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>
                              {field.fieldName || field.fieldKey}
                              {field.isRequired && <span style={{ color: '#EF4444' }}>*</span>}
                            </label>
                            
                            {fieldType === 'text' && (
                              <input
                                type="text"
                                value={value}
                                onChange={(e) => handleCustomFieldChange(field.fieldKey, e.target.value)}
                                placeholder={`Enter ${field.fieldName || field.fieldKey}`}
                                style={{
                                  width: '100%',
                                  padding: '8px 12px',
                                  backgroundColor: isManuallyChanged ? '#FEF3C7' : '#F9FAFB',
                                  border: isManuallyChanged ? '2px solid #F59E0B' : '1px solid #E5E7EB',
                                  borderRadius: '6px',
                                  outline: 'none',
                                  fontSize: '14px',
                                  color: '#111827',
                                  boxSizing: 'border-box'
                                }}
                              />
                            )}

                            {fieldType === 'textarea' && (
                              <textarea
                                value={value}
                                onChange={(e) => handleCustomFieldChange(field.fieldKey, e.target.value)}
                                placeholder={`Enter ${field.fieldName || field.fieldKey}`}
                                rows={3}
                                style={{
                                  width: '100%',
                                  padding: '8px 12px',
                                  backgroundColor: isManuallyChanged ? '#FEF3C7' : '#F9FAFB',
                                  border: isManuallyChanged ? '2px solid #F59E0B' : '1px solid #E5E7EB',
                                  borderRadius: '6px',
                                  outline: 'none',
                                  fontSize: '14px',
                                  color: '#111827',
                                  boxSizing: 'border-box',
                                  resize: 'vertical'
                                }}
                              />
                            )}

                            {fieldType === 'number' && (
                              <input
                                type="number"
                                value={value}
                                onChange={(e) => handleCustomFieldChange(field.fieldKey, e.target.value)}
                                placeholder={`Enter ${field.fieldName || field.fieldKey}`}
                                style={{
                                  width: '100%',
                                  padding: '8px 12px',
                                  backgroundColor: isManuallyChanged ? '#FEF3C7' : '#F9FAFB',
                                  border: isManuallyChanged ? '2px solid #F59E0B' : '1px solid #E5E7EB',
                                  borderRadius: '6px',
                                  outline: 'none',
                                  fontSize: '14px',
                                  color: '#111827',
                                  boxSizing: 'border-box'
                                }}
                              />
                            )}

                            {fieldType === 'date' && (
                              <input
                                type="date"
                                value={value}
                                onChange={(e) => handleCustomFieldChange(field.fieldKey, e.target.value)}
                                style={{
                                  width: '100%',
                                  padding: '8px 12px',
                                  backgroundColor: isManuallyChanged ? '#FEF3C7' : '#F9FAFB',
                                  border: isManuallyChanged ? '2px solid #F59E0B' : '1px solid #E5E7EB',
                                  borderRadius: '6px',
                                  outline: 'none',
                                  fontSize: '14px',
                                  color: '#111827',
                                  boxSizing: 'border-box'
                                }}
                              />
                            )}

                            {fieldType === 'dropdown' && options.length > 0 && (
                              <select
                                value={value}
                                onChange={(e) => handleCustomFieldChange(field.fieldKey, e.target.value)}
                                style={{
                                  width: '100%',
                                  padding: '8px 12px',
                                  backgroundColor: isManuallyChanged ? '#FEF3C7' : '#F9FAFB',
                                  border: isManuallyChanged ? '2px solid #F59E0B' : '1px solid #E5E7EB',
                                  borderRadius: '6px',
                                  outline: 'none',
                                  fontSize: '14px',
                                  color: '#111827',
                                  cursor: 'pointer',
                                  boxSizing: 'border-box'
                                }}
                              >
                                <option value="">Select {field.fieldName || field.fieldKey}</option>
                                {options.map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                            )}

                            {fieldType === 'checkbox' && (
                              <div>
                                {options.length > 0 ? (
                                  options.map((option) => (
                                    <label key={option} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                      <input
                                        type="checkbox"
                                        checked={value && value.includes(option)}
                                        onChange={(e) => {
                                          let newValue = value ? value.split(',') : [];
                                          if (e.target.checked) {
                                            newValue.push(option);
                                          } else {
                                            newValue = newValue.filter(item => item !== option);
                                          }
                                          handleCustomFieldChange(field.fieldKey, newValue.join(','));
                                        }}
                                      />
                                      {option}
                                    </label>
                                  ))
                                ) : (
                                  <input
                                    type="text"
                                    value={value}
                                    onChange={(e) => handleCustomFieldChange(field.fieldKey, e.target.value)}
                                    placeholder="Enter checkbox options"
                                    style={{
                                      width: '100%',
                                      padding: '8px 12px',
                                      backgroundColor: isManuallyChanged ? '#FEF3C7' : '#F9FAFB',
                                      border: isManuallyChanged ? '2px solid #F59E0B' : '1px solid #E5E7EB',
                                      borderRadius: '6px',
                                      outline: 'none',
                                      fontSize: '14px',
                                      color: '#111827',
                                      boxSizing: 'border-box'
                                    }}
                                  />
                                )}
                              </div>
                            )}

                            {fieldType === 'radio' && options.length > 0 && (
                              <div>
                                {options.map((option) => (
                                  <label key={option} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                    <input
                                      type="radio"
                                      name={`custom_${field.fieldKey}`}
                                      value={option}
                                      checked={value === option}
                                      onChange={(e) => handleCustomFieldChange(field.fieldKey, e.target.value)}
                                    />
                                    {option}
                                  </label>
                                ))}
                              </div>
                            )}

                            {isManuallyChanged && (
                              <div style={{ fontSize: '11px', color: '#D97706', marginTop: '4px' }}>
                                ⚠️ Field changed - please provide reason when saving
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Summary of changed fields - only showing leadCategory, stage, probability */}
              {changedFieldKeys.length > 0 && (
                <div style={{
                  marginTop: '16px',
                  padding: '12px 16px',
                  backgroundColor: '#FEF3C7',
                  borderRadius: '8px',
                  border: '1px solid #F59E0B'
                }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#92400E' }}>
                    ⚠️ {changedFieldKeys.length} field(s) changed:
                  </div>
                  <div style={{ fontSize: '12px', color: '#78350F', marginTop: '4px' }}>
                    {changedFieldKeys.map(k => fieldLabels[k] || k).join(', ')}
                  </div>
                  <div style={{ fontSize: '12px', color: '#78350F', marginTop: '4px' }}>
                    Please provide reasons when saving
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #F3F4F6',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px'
        }}>
         { activeTab===('edit'||'view')&&<button
            onClick={onClose}
            style={{
              padding: '8px 20px',
              backgroundColor: '#F9FAFB',
              border: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              color: '#374151',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            {activeTab === 'edit' ? 'Cancel' : 'Close'}
          </button>}
          {activeTab === 'edit' && (
            <button
              onClick={handleSaveWithReasons}
              disabled={isSaving}
              style={{
                padding: '8px 20px',
                backgroundColor: '#7C3AED',
                borderRadius: '8px',
                fontWeight: '600',
                color: '#fff',
                cursor: isSaving ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                opacity: isSaving ? 0.7 : 1
              }}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          )}
            {activeTab === 'quotes' && (
    <QuotesTab 
      leadId={formData._id} 
      leadName={formData.name}
      leadPhone={formData.mobile || formData.phone || formData.contactNumber || ''}
      leadEmail={formData.email || ''}
      onClose={onClose}
    />
  )}
  {activeTab === 'invoices' && (
    <InvoicesTab 
      leadId={formData._id}
      leadName={formData.name}
      leadPhone={formData.mobile || formData.phone || formData.contactNumber || ''}
      leadEmail={formData.email || ''}
      userRole={userRole || 'Owner'}
      onClose={onClose}
    />
  )}
        </div>
      </div>

      {/* Reasons Modal - Only for leadCategory, stage, probability */}
      {showReasonsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 2000,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            width: '550px',
            maxWidth: '95%',
            maxHeight: '90vh',
            padding: '24px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            overflowY: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                Reasons for Changes <span style={{ color: '#EF4444' }}>*</span>
              </h3>
              <button
                onClick={() => {
                  setShowReasonsModal(false);
                }}
                style={{
                  background: 'none',
                  cursor: 'pointer',
                  color: '#6B7280',
                  fontSize: '20px'
                }}
              >
                ×
              </button>
            </div>
            
            <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '16px' }}>
              Please provide a reason for each field you manually changed.
              <br />
              <span style={{ fontSize: '12px', color: '#10B981' }}>
                ✓ Only Lead Category, Stage, and Probability require reasons.
              </span>
              <br />
              <span style={{ fontSize: '12px', color: '#F59E0B' }}>
                ⚠️ Probability changes from Task Complete are auto-updated and don't require a reason.
              </span>
            </p>
            
            {changedFieldKeys.length > 0 ? (
              changedFieldKeys.map((field) => (
                <div key={field} style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>
                    {fieldLabels[field] || field} <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={fieldReasons[field] || ''}
                    onChange={(e) => handleReasonChange(field, e.target.value)}
                    placeholder={`Reason for changing ${fieldLabels[field] || field}`}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      outline: 'none',
                      fontSize: '14px',
                      color: '#111827',
                      boxSizing: 'border-box'
                    }}
                    autoFocus={field === changedFieldKeys[0]}
                  />
                  {fieldReasons[field] && fieldReasons[field].trim() === '' && (
                    <div style={{ fontSize: '11px', color: '#EF4444', marginTop: '4px' }}>
                      Reason is required
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div style={{ textAlign: 'center', padding: '20px 0', color: '#6B7280' }}>
                No changes that require reasons.
              </div>
            )}
            
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              marginTop: '20px',
              paddingTop: '16px',
              borderTop: '1px solid #F3F4F6'
            }}>
              <button
                onClick={() => {
                  setShowReasonsModal(false);
                }}
                style={{
                  padding: '8px 20px',
                  backgroundColor: '#F9FAFB',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  color: '#374151',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateLead}
                disabled={changedFieldKeys.some(field => !fieldReasons[field] || !fieldReasons[field].trim())}
                style={{
                  padding: '8px 20px',
                  backgroundColor: changedFieldKeys.some(field => !fieldReasons[field] || !fieldReasons[field].trim()) ? '#9CA3AF' : '#7C3AED',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  color: '#fff',
                  cursor: changedFieldKeys.some(field => !fieldReasons[field] || !fieldReasons[field].trim()) ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                Save All Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditLeadModal;